import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { createBrowserSupabaseClient } from '../lib/supabase';

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  configured: boolean;
  signUp: (email: string, password: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

function authRedirectTo() {
  return `${window.location.origin}/auth/callback`;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getClient(): SupabaseClient | null {
  try {
    return createBrowserSupabaseClient();
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useMemo(getClient, []);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(Boolean(client));

  useEffect(() => {
    if (!client) return;
    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setLoading(false);
      }
    });
    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    configured: Boolean(client),
    async signUp(email, password) {
      if (!client) throw new Error('Authentication is not configured yet.');
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: authRedirectTo() }
      });
      if (error) throw new Error('We could not create that account. Please check your details and try again.');
      return { needsEmailConfirmation: !data.session };
    },
    async signIn(email, password) {
      if (!client) throw new Error('Authentication is not configured yet.');
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw new Error('We could not log you in with those details.');
    },
    async signInWithGoogle() {
      if (!client) throw new Error('Authentication is not configured yet.');
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: authRedirectTo(),
          queryParams: { prompt: 'select_account' },
        },
      });
      if (error) {
        const disabled = /provider is not enabled|unsupported provider/i.test(error.message);
        throw new Error(
          disabled
            ? 'Google sign-in is not enabled yet. Turn on the Google provider in Supabase Auth.'
            : 'We could not start Google sign-in. Please try again.',
        );
      }
    },
    async signOut() {
      if (client) await client.auth.signOut();
      setSession(null);
    }
  }), [client, loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
